<template>
  <section v-if="visible" class="what-you-get">
    <header class="what-you-get__head">
      <span class="what-you-get__title text-body-1 text-sans text-bold">
        {{ $t("unit_page.un_what_you_get_title") }}
      </span>
      <span class="what-you-get__intro text-body-2 text-sans text-regular">
        {{ $t("unit_page.un_what_you_get_intro") }}
      </span>
    </header>

    <ul class="what-you-get__list">
      <li v-for="(row, idx) in rows" :key="idx" class="what-you-get__row">
        <span class="what-you-get__row-key text-body-2 text-regular">
          {{ row.key }}
        </span>
        <span class="what-you-get__row-val text-body-2 text-bold">
          {{ row.value }}
        </span>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { BASE_CURRENCY, HUGE_CURRENCY } from "~/composables/CURRENCIES";
import type { UnitResponseDTO } from "~/services/swagger/Api";

interface IProps {
  propertyInfo: UnitResponseDTO | null | undefined;
}

const props = defineProps<IProps>();
const { t } = useI18n();

const convertFromUSD = (
  usd: number | null,
  nativeValue?: number | string | null,
  nativeCurrency?: string | null
): number | null => {
  if (usd === null) return null;
  const base = BASE_CURRENCY.value;
  if (nativeCurrency && nativeCurrency === base.code) {
    const n = typeof nativeValue === "string" ? parseFloat(nativeValue) : nativeValue;
    if (typeof n === "number" && !Number.isNaN(n) && n > 0) return n;
  }
  return usd * base.rate;
};

const fractionalOwnership = computed(() => {
  const total = Number(props.propertyInfo?.tokenomics?.totalSupply ?? 0);
  if (!total || total <= 0) return null;
  const pct = 1 / total;
  if (pct < 1e-6) return `${(pct * 1e6).toFixed(2)} ppm`;
  if (pct < 1e-4) return `${(pct * 100).toFixed(6)}%`;
  return `${(pct * 100).toFixed(4)}%`;
});

const tokenPriceUSDNumber = computed<number | null>(() => {
  const usd = props.propertyInfo?.tokenomics?.priceUSD;
  if (!usd) return null;
  const n = Number(usd);
  return Number.isFinite(n) && n > 0 ? n : null;
});

const tokenPrice = computed(() => {
  const value = convertFromUSD(
    tokenPriceUSDNumber.value,
    props.propertyInfo?.tokenomics?.price,
    props.propertyInfo?.tokenomics?.currency ?? props.propertyInfo?.currency
  );
  if (value === null) return null;
  const base = BASE_CURRENCY.value;
  if (HUGE_CURRENCY.includes(base.code) && value >= 10000) {
    return `${base.symbol}${Math.round(value).toLocaleString("en-US")}`;
  }
  return value < 1
    ? `${base.symbol}${value.toFixed(2)}`
    : `${base.symbol}${Math.round(value).toLocaleString("en-US")}`;
});

const apr = computed(() => {
  const a = props.propertyInfo?.apr;
  if (typeof a !== "number" || a <= 0) return null;
  return Math.round(a * 10) / 10;
});

const annualReturn = computed(() => {
  const priceUSD = tokenPriceUSDNumber.value;
  const a = apr.value;
  if (!priceUSD || !a) return null;
  const retUSD = (priceUSD * a) / 100;
  // Native annual return (when base matches token currency) keeps precision.
  const nativePrice = props.propertyInfo?.tokenomics?.price;
  const nativeRet =
    typeof nativePrice === "number" && nativePrice > 0
      ? (nativePrice * a) / 100
      : undefined;
  const ret = convertFromUSD(
    retUSD,
    nativeRet,
    props.propertyInfo?.tokenomics?.currency ?? props.propertyInfo?.currency
  );
  if (ret === null) return null;
  const base = BASE_CURRENCY.value;
  if (ret < 0.01) return `${base.symbol}${ret.toFixed(4)}/yr`;
  if (ret < 1) return `${base.symbol}${ret.toFixed(2)}/yr`;
  return `${base.symbol}${Math.round(ret).toLocaleString("en-US")}/yr`;
});

const chain = computed(() => {
  const c = (props.propertyInfo as any)?.contract?.chain?.name;
  return typeof c === "string" && c ? c.toUpperCase() : null;
});

const rows = computed(() => {
  try {
    const out: Array<{ key: string; value: string }> = [];
    if (tokenPrice.value) {
      out.push({ key: t("search_card.fr_token"), value: tokenPrice.value });
    }
    if (fractionalOwnership.value) {
      out.push({
        key: t("unit_page.un_what_you_get_share"),
        value: fractionalOwnership.value,
      });
    }
    if (annualReturn.value) {
      out.push({
        key: t("unit_page.un_what_you_get_annual"),
        value: annualReturn.value,
      });
    }
    if (chain.value) {
      out.push({
        key: t("unit_page.un_what_you_get_chain"),
        value: chain.value,
      });
    }
    return out;
  } catch {
    return [];
  }
});

const visible = computed(() => rows.value.length > 0);
</script>

<style scoped lang="scss">
.what-you-get {
  width: 100%;
  padding: 16px;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: var(--shadow-card);

  &__head {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__title {
    color: var(--text-primary);
  }

  &__intro {
    color: var(--text-secondary);
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 0;
    border-bottom: 1px dashed var(--gray-monochrome-20);
    &:last-child {
      border-bottom: none;
    }
  }

  &__row-key {
    color: var(--black-monochrome-72);
  }

  &__row-val {
    color: var(--black-monochrome);
  }
}
</style>
